import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { filter, uniq, map } from 'lodash';
import { getToken } from 'firebase/messaging';

import * as StampCardService from '../../api/stampCardService';
import * as NotificationService from '../../api/notificationService';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';

import LoyaltyCard from '../../components/Card/LoyaltyCard';

import useDeepLinkContext from '../../hooks/useDeepLinkContext';
import useDeepLinkHandler from '../../hooks/useDeepLinkHandler';
import { messaging } from '../../lib/authHook/firebase';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  grow: {
    flexGrow: 1,
  },
  noStamp: {
    marginTop: theme.spacing(8),
  },
  dialogTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    marginLeft: theme.spacing(),
  },
}));

/**
 * Root redirects to the user's companies dashboard.
 */
export default function Root() {
  const classes = useStyles();
  const location = useLocation();
  const params = queryString.parse(location.search);
  const feature = params['feature'];
  const merchantId = params['merchant'];
  const contactlessCode = params['code'];
  const couponCode = params['code'];
  const merchantCode = params['merchant'];
  const userCode = params['user'];
  const singleUseCode = params['code'];
  const store = params['store'];
  const stampNum = params['numOfStamps'];
  const [stampCardList, setStampCardList] = useState<
    StampCardService.MemberStampCard[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const dl = useDeepLinkContext();
  const initialized = useRef(false);
  const [merchantList, setMerchantList] = useState<string[]>();
  const [requestToken, setRequestToken] = useState(true);
  const VAPID_PUBLIC_KEY = process.env.REACT_APP_FIREBASE_VAP_ID as string;

  const requestPermission = useCallback(async () => {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      // Generate Device Token for notification
      const token = await getToken(messaging, {
        vapidKey: VAPID_PUBLIC_KEY,
      });

      console.log('Token Gen', token);
      if (token.length > 0) {
        try {
          await NotificationService.register(token, 'browser');
          setRequestToken(false);
        } catch (e) {
          console.log('Error:', e);
        }
      }
    } else if (permission === 'denied') {
      console.log('Denied for the notification');
      setRequestToken(false);
    }
  }, [VAPID_PUBLIC_KEY]);

  useEffect(() => {
    if (requestToken && !isLoading) {
      requestPermission();
    }
  }, [requestToken, requestPermission, isLoading]);

  useEffect(() => {
    const setDeeplink = () => {
      if (dl && isLoading && !initialized.current) {
        initialized.current = true;
        dl.setDeeplink({
          feature: feature && typeof feature == 'string' ? feature : undefined,
          merchantId:
            merchantId && typeof merchantId == 'string'
              ? merchantId
              : undefined,
          contactlessCode:
            contactlessCode && typeof contactlessCode == 'string'
              ? contactlessCode
              : undefined,
          couponCode:
            couponCode && typeof couponCode == 'string'
              ? couponCode
              : undefined,
          merchantCode:
            merchantCode && typeof merchantCode == 'string'
              ? merchantCode
              : undefined,
          userCode:
            userCode && typeof userCode == 'string' ? userCode : undefined,
          singleUseCode:
            singleUseCode && typeof singleUseCode == 'string'
              ? singleUseCode
              : undefined,
          store: store && typeof store == 'string' ? store : undefined,
          stampNum:
            stampNum && typeof stampNum == 'string' ? stampNum : undefined,
        });
      }
      setIsLoading(false);
    };

    if (isLoading) {
      setDeeplink();
    }
  }, [
    isLoading,
    feature,
    merchantId,
    contactlessCode,
    couponCode,
    merchantCode,
    userCode,
    dl,
    singleUseCode,
    store,
    stampNum,
  ]);

  useEffect(() => {
    const fetch = async () => {
      const resList = await StampCardService.list();
      let cardList: StampCardService.MemberStampCard[] = [];

      if (resList) {
        console.log(resList);
        if (resList.length === 1 && resList[0].stampCards.length === 0) {
          setStampCardList([]);

          setMerchantList([resList[0].merchantID]);
          setIsLoading(false);
        } else {
          resList.forEach((member, idx) => {
            let cl = member.stampCards.map((card, index) => {
              return {
                id: card.id,
                createdAt: card.createdAt,
                quantity: card.quantity,
                redemptionCost: card.redemptionCost,
                status: card.status,
                updatedAt: card.updatedAt,
                slotNumber: card.slotNumber,
                stampCampaignName: card.stampCampaignName,
                stampCampaignType: card.stampCampaignType,
                stampCampaignDescription: card.stampCampaignDescription,
                merchantId: member.merchantID,
                showVisitedStores: card.showVisitedStores,
                loyalty_campaign: {
                  ID: card.loyalty_campaign.ID,
                },
                reward: {
                  ID: card.reward.ID,
                },
              };
            });
            cardList = cardList.concat(cl);
          });

          setStampCardList(cardList);
          let mList = map(resList, 'merchantID');

          setMerchantList(uniq(mList));
          setIsLoading(false);
        }
      }
    };

    // setDevice(detectDevice);
    fetch();
  }, []);

  useDeepLinkHandler();

  if (isLoading) {
    return null;
  }

  return (
    <>
      <div className={classes.grow} />
      <div className={classes.container}>
        {merchantList && merchantList.length > 0 ? (
          merchantList.map((merchantId, idx) => {
            return (
              <LoyaltyCard
                key={idx}
                merchantId={merchantId}
                cardList={filter(stampCardList, function (card) {
                  return card.merchantId === merchantId;
                })}
              />
            );
          })
        ) : (
          <Typography
            variant="h5"
            color="textSecondary"
            className={classes.noStamp}
          >
            No loyalty cards yet
          </Typography>
        )}
      </div>
    </>
  );
}
